// Centralized Appointment Service - Single source of truth for all appointments
// This connects Calendar, Appointments, Queue, and Check-in modules

import { clientDataService } from './clientDataService';
import { appointmentDedupeKey } from '../shared/appointmentKey';
import { hitsNoShowBlock, DEFAULT_NO_SHOW_NOTICE } from '../shared/noShowBlock';
import { mergeBookingRules } from '../shared/bookingRules';
import { findSamePerson, normalizePhone } from '../shared/identity';
import { localAIService } from './LocalAIService';
import { shopSettingsService, type BusinessHours } from './shopSettingsService';
import { barberDataService } from './barberDataService';
import { ymd, parseYmd } from '../shared/dateUtils';

import { parseTimeToMinutes, hasApptConflict, resolveDayHours, applyShift, isDayEnabled } from '../shared/availability';
import { shiftService } from './shiftService';
import { getOperatorBarberId } from './operatorSession';

export interface Appointment {
  id: string;
  clientId: string;  // WHO the appointment is for (parent OR family member)
  clientName: string;
  clientPhone: string;  // Phone number (shared across family)
  clientEmail?: string; // Email address (for closure notifications)
  barberId: string;
  barberName: string;
  service: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  duration: number; // in minutes
  price: number;
  status: 'scheduled' | 'confirmed' | 'checked-in' | 'in-progress' | 'completed' | 'paid' | 'cancelled' | 'no-show';
  // 💸 PER-SERVICE DISCOUNTS (Jose 2026-08-09): chosen in the appointment card (dropdown, like Duration),
  // instant strikethrough. price stays ORIGINAL (reporting/PPI honesty); the checkout applies these +
  // runs the existing fraud gate per service. See src/utils/discount.ts.
  serviceDiscounts?: Array<{
    serviceName: string;
    originalPrice: number;
    discountedPrice: number;
    discountAmount: number;
    reasonId: string;
    reasonLabel: string;
    type: 'comp' | 'percent' | 'fixed';
    value: number;
  }>;
  // 💲 PER-SERVICE BREAKDOWN (Jose 2026-10-21): the actual per-service rows + their (possibly manually
  // set) prices for a multi-service appointment. Persisted so a manual price survives a reopen and feeds
  // checkout from the real source — WITHOUT this, the card re-derived prices from the catalog each open
  // and silently reverted a manual change. The combined `price` above stays the sum of these.
  serviceItems?: Array<{ name: string; price: number; duration?: number }>;
  notes?: string;
  noShowReason?: string; // Reason for no-show status
  // 🔁 RECURRING REBOOK (Jose 2026-08-10): every appointment in a recurring series shares this id, so
  // the series can later be adjusted as a set ("update the rest"). recurrenceIndex = 0-based position.
  recurrenceGroupId?: string;
  recurrenceIndex?: number;
  // ♾️ FOREVER (Jose 2026-10-15): an ongoing standing series (not a fixed 3/6/12-mo run) — keeps going
  // until changed; renders the ♾️ badge. Family bundles (Fresha "Father + Son") tagged for a later split.
  forever?: boolean;
  familyBundle?: boolean;
  bundleHeadcount?: number;
  needsFamilySplit?: boolean;
  familyBundleSameBarber?: boolean;
  // 🔁 REBOOK TRACE (Jose 2026-09-27): a SINGLE rebook booked at checkout carries source:'rebook' +
  // rebookedFromAppointmentId (the completed appt it came from) so rebooking is a REAL per-barber signal,
  // not inferred from history. A recurring SERIES is the distinct case, marked by recurrenceGroupId (above).
  rebookedFromAppointmentId?: string;
  rebookedAt?: Date;
  createdAt: Date;
  checkedInAt?: Date;
  startedAt?: Date;
  completedAt?: Date;
  actualDuration?: number; // Real minutes from startedAt → completedAt (powers squeeze-in intelligence)
  source: 'online' | 'phone' | 'walk-in' | 'app' | 'import' | 'emergency' | 'rebook'; // 🚨 'emergency' = portal booking; 🔁 'rebook' = created via checkout "Rebook →" (per-barber rebooking signal)
  // 🧾 RETAIL PRODUCT SALE (Jose 2026-09-03): a product-only purchase (no service). It's a TRANSACTION,
  // not a calendar appointment — hidden from the calendar day grid + Status Board, but the record still
  // exists so product revenue (productsSold) and barber product-credit still flow into reporting.
  saleType?: 'product';
  reminderSent: boolean;
  isNewClient?: boolean; // 🆕 First-timer (not seen before) — 45-min appt + "New Client" label (2026-08-07)
  emergencyBooking?: boolean; // 🚨 Global emergency flag for red glow visual
  emergencyFeeWaived?: boolean; // ✅ emergency fee waived from the client's profile (Jose 2026-10-21)
  squeezedIn?: boolean; // ⚡ manager squeeze (allowOverlap) — referee + guards leave it alone
  urgencyLevel?: 'standard' | 'urgent' | 'critical' | 'high'; // 🚨 Emergency urgency level
  missedCheckoutWindow?: boolean; // 🕵️ Anti-fraud: a barber tried to check this out past its window — flagged for manager review (Jose 2026-08-17)
  voidReason?: string;   // 🧾 Set when a completed sale is voided from the checkout card (status → cancelled), manager-gated (Jose 2026-08-19)
  voidedAt?: Date;       // when the sale was voided
  // 💳 STRIPE (Jose 2026-08-21) — set by the stripeWebhook Cloud Function when a payment-link is paid.
  // stripePaymentIntentId is what makes a real card refund possible (createRefund refunds this PI).
  paidOnline?: boolean;          // paid via a Stripe payment link (vs external terminal / cash)
  stripeSessionId?: string;      // Stripe Checkout Session id
  stripePaymentIntentId?: string; // Stripe PaymentIntent id — refund target
  refundedAt?: Date;             // when a refund was issued (refundAmount already exists above)
  // 💳 PAYMENT-LINK AUTO-COMPLETE (Jose 2026-08-21) — when a barber texts a payment link we stash a
  // pending snapshot here (NO side effects, safe if never paid); stripeWebhook completes the sale the
  // instant Stripe confirms payment, so a PAID link lands in reporting + barber credit with no manual
  // step. Jose's call: the sale counts "when the customer pays", not when the link is sent.
  // BEHAVIOR-PIN: project-slik-pos-payments-sms-integration-2026-08-21
  paymentStatus?: 'awaiting_online' | 'paid'; // 'awaiting_online' = link sent, money not yet in
  paymentLinkSentAt?: Date;      // when the payment link was sent
  pendingPayment?: {             // snapshot the webhook completes the sale from, on payment
    total: number;
    tipAmount: number;
    productsSold?: any[];
    discountAmount?: number;
    discountReason?: string;
    discountNotes?: string;
    paymentMethod: 'mobile';
  };
  emergencyBookingId?: string; // Link to emergency booking record (if from customer portal)

  // 🧾 PROVENANCE — who created this appointment (tapped-in operator at the station). WHEN=createdAt,
  // HOW=source, WHO=createdByName. Blank for online/import/portal (source carries it). (Jose 2026-10-29)
  createdByBarberId?: string;
  createdByName?: string;

  // 👨‍👩‍👧‍👦 FAMILY BOOKING CONTEXT
  bookedBy?: string;  // ID of person who booked (if parent booked for child)
  isForFamilyMember?: boolean;  // TRUE if parent booked for child
  // Example: Parent "John Smith" books for son "Tommy Smith"
  // clientId: "tommy-123", clientName: "Tommy Smith", bookedBy: "john-456", isForFamilyMember: true

  // 📍 GEO-LOCATION TRACKING — "On My Way" ETA Feature
  geoLocation?: {
    enabled: boolean;
    latitude?: number;
    longitude?: number;
    eta?: number; // Estimated time of arrival in minutes
    lastUpdated?: Date;
    status?: 'on-way' | 'late-reported' | 'arrived' | 'cancelled';
    trackingStarted?: Date;
    lateReported?: boolean;
    manualEta?: number; // Client-reported delay in minutes (+10, +20, +30)
    staffResponse?: 'keep-slot' | 'take-walkin' | 'reschedule';
    staffResponseAt?: Date;
    rescheduledTo?: string; // HH:MM if rescheduled
  };

  // 🚫 EMERGENCY BOOKING BLOCK (Per-client setting for long haircuts)
  blockEmergencyBookings?: boolean; // TRUE if this client blocks emergency bookings during their appointment
  startTime?: string; // HH:MM format (for time range calculations)
  endTime?: string; // HH:MM format (for time range calculations)

  // 📍 MULTI-LOCATION SUPPORT
  locationId?: string; // Which location this appointment is at (e.g., 'loc-barber-lounge', 'loc-hair-studio')

  // 💰 CHECKOUT & PAYMENT DATA (Added for Firestore Reporting Integration)
  tipAmount?: number;           // Tip amount in dollars
  paymentMethod?: 'cash' | 'card' | 'mobile' | 'store_credit' | 'gift_card';
  splitCashAmount?: number;     // 💵 SPLIT TENDER (Jose 2026-10-30): cash collected alongside the card. Reporting peels this to `cash`; `paymentMethod` keeps the remainder (card/mobile). amountCharged = the card portion only.
  discountAmount?: number;      // Discount applied at checkout
  discountReason?: string;      // Why discount was given (service_issue, loyalty, etc.)
  discountNotes?: string;       // Barber's note explaining the discount
  refundAmount?: number;        // If any refund was issued
  productsSold?: Array<{        // Products sold during checkout
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    total: number;
    barberId?: string;          // PER-BARBER CREDIT: who SOLD this product (may differ from the ticket's barber)
    barberName?: string;
  }>;
}

export interface QueueItem {
  id: string;
  appointmentId?: string;
  clientName: string;
  clientPhone?: string;
  service: string;
  barberId?: string;
  barberName?: string;
  estimatedWait: number; // in minutes
  checkedInAt: Date;
  status: 'waiting' | 'next' | 'in-service' | 'completed';
  isWalkIn: boolean;
}

// Pure appointment identity (../shared/appointmentKey, no Firebase) — imported at top, re-exported here
// for existing consumers.
export { appointmentDedupeKey };

class AppointmentService {
  private appointments: Appointment[] = [];
  private queue: QueueItem[] = [];
  private listeners: Set<() => void> = new Set();
  // Track appointments with pending Firebase updates and their expected values
  private pendingUpdates: Map<string, Record<string, any>> = new Map();
  // TOMBSTONES (2026-08-07): ids the user just deleted. The snapshot merge re-adds any doc that's
  // "in Firebase but not local" — without this, a delete that's still in-flight (or racing a
  // snapshot) gets resurrected, causing the ping-pong "it won't go away". Pruned once Firebase
  // confirms the doc is gone; cleared if the delete actually fails so we never hide a live doc.
  private deletedIds: Set<string> = new Set();
  // IDEMPOTENCY (Jose 2026-08-18): identical creates fired CONCURRENTLY (a double-click / double-handler)
  // both pass the conflict check before either has written → two duplicate documents. Collapse them by
  // returning the first in-flight create's promise for the same identity. Sequential creates (loops,
  // separate bookings) are never affected — the key is cleared the moment a create settles.
  private inflightCreates: Map<string, Promise<Appointment>> = new Map();
  // LOAD-THE-MINIMUM (Jose 2026-10-23): the live real-time subscription holds only a tight recent window
  // + ALL future (set in initializeFirebaseSync). `liveCutoff` = the window's start date (YYYY-MM-DD) — the
  // snapshot never includes older docs, so the merge KEEPS locally-held older ones (on-demand past) instead
  // of evicting them. `earliestLoaded` = how far back ensureDateRange() has pulled on demand (starts at the
  // cutoff). Future is always loaded, so forward navigation never needs a fetch.
  private liveCutoff = '';
  private earliestLoaded = '';
  private inflightRangeFetch: Promise<void> | null = null;
  // Unsubscribe handle for the live window listener, so a re-subscribe (on auth-ready) never stacks a
  // second listener. (Jose 2026-10-29 audit — pre-auth-subscribe resilience, same class as shiftService.)
  private _apptUnsub: (() => void) | null = null;
  // Last Firestore write failure (Jose 2026-08-30): a completed SALE that fails to persist falls back
  // to a local-only record that a later snapshot wipes — money moved, record gone. We stash the exact
  // error here so the checkout UI can surface it on-screen (no console needed) and never lose it silently.
  public lastWriteError: string | null = null;

  constructor() {
    // Don't load from localStorage - let Firebase be the source of truth
    // This prevents stale data from overwriting fresh Firebase data
    localStorage.removeItem('slikpos_appointments');
    localStorage.removeItem('slikpos_queue');

    this.initializeSampleData();

    // Connect to Firebase for real-time updates (primary data source).
    // Skip in the jest/unit-test env — opening live Firestore/gRPC in jsdom crashes on setImmediate,
    // and unit tests must never hit the live DB. Production/build (NODE_ENV!=='test') runs normally.
    if (process.env.NODE_ENV !== 'test') {
      this.initializeFirebaseSync();
      // RESILIENCE (Jose 2026-10-29 audit): the sync above runs at MODULE LOAD, which can be BEFORE Firebase
      // auth is ready → the window read is permission-denied and (with no error cb) the listener dies silently
      // → the calendar would show no appointments for the whole session (the exact shiftService failure mode).
      // Re-subscribe the moment auth resolves so a denied pre-auth read self-heals. Dynamic import keeps the
      // constructor synchronous + avoids circular deps. (unsub-prior inside initializeFirebaseSync prevents double listeners.)
      import('firebase/auth').then(({ onAuthStateChanged }) =>
        import('../config/firebase').then(({ auth }) => {
          onAuthStateChanged(auth, (u) => { if (u) this.initializeFirebaseSync(); });
        })
      ).catch(() => {});
    }
  }

  // Initialize Firebase sync for real-time appointments
  private async initializeFirebaseSync() {
    try {
      // Tear down any prior window listener before (re)subscribing — on an auth-ready re-subscribe this
      // prevents a second live listener stacking on the first. (Jose 2026-10-29 audit)
      try { this._apptUnsub?.(); } catch { /* ignore */ }
      this._apptUnsub = null;
      // Import Firebase services dynamically to avoid circular dependencies
      const { db } = await import('../config/firebase');
      const { collection, onSnapshot, orderBy, where } = await import('firebase/firestore');
      const { scopedQuery, ensureActiveShopId } = await import('./tenantQuery');
      await ensureActiveShopId(); // resolve the shop BEFORE subscribing so the listener is shop-scoped

      // Listen to real-time updates from Firebase
      const appointmentsRef = collection(db, 'appointments');
      // PERF (2026-09-01): bound the live real-time subscription to a recent+forward window.
      // The Fresha historical import grew `appointments` to ~18.8k docs (2024–2026); loading the
      // ENTIRE collection on boot hung the app ("spinning, never loads"). The live calendar/POS only
      // needs recent past + all future. Reports read history via reportingDataService's OWN
      // date-range Firestore query (independent of this cache), so bounding here does NOT affect them.
      // LOAD-THE-MINIMUM (Jose 2026-10-23): was −180d (6,119 docs loaded every boot on a 20.8k collection,
      // the "spinning / sluggish" cause). Now a TIGHT recent window (−21d) + all future. Older days load
      // on demand via ensureDateRange() when the calendar navigates to them — so nothing disappears, boot
      // just stops hauling 6 months of history nobody's looking at live. Future stays fully live.
      const WINDOW_BACK_DAYS = 21;
      const _lb = new Date(); _lb.setDate(_lb.getDate() - WINDOW_BACK_DAYS);
      const cutoff = `${_lb.getFullYear()}-${String(_lb.getMonth() + 1).padStart(2, '0')}-${String(_lb.getDate()).padStart(2, '0')}`;
      this.liveCutoff = cutoff;
      this.earliestLoaded = cutoff;
      const q = scopedQuery(appointmentsRef, where('date', '>=', cutoff), orderBy('date', 'asc'));

      let isFirstSnapshot = true;

      this._apptUnsub = onSnapshot(q, (snapshot) => {
        const firebaseAppointments: Appointment[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          const appointment = {
            ...data,
            // ID-CLOBBER FIX (2026-08-07): spread data FIRST, then force id = the Firestore doc id.
            // If a doc ever carries an internal `id` field, `id: doc.id` before `...data` let it
            // OVERWRITE the real key — then updateDoc/deleteDoc targeted a non-existent doc (delete
            // silently no-ops), so deletes never stuck and the merge resurrected them. (Same trap as
            // the client mapper.) id MUST be the doc id for writes/deletes to hit the right record.
            id: doc.id,
            createdAt: data.createdAt?.toDate() || new Date(),
            checkedInAt: data.checkedInAt?.toDate(),
            startedAt: data.startedAt?.toDate(),
            completedAt: data.completedAt?.toDate()
          } as Appointment;

          firebaseAppointments.push(appointment);
        });

        // On first snapshot, if Firebase is empty, don't overwrite local data
        if (isFirstSnapshot && firebaseAppointments.length === 0 && this.appointments.length > 0) {
          isFirstSnapshot = false;
          return;
        }

        isFirstSnapshot = false;

        // Intelligent merge strategy - prevent data loss
        const firebaseMap = new Map(firebaseAppointments.map(a => [a.id, a]));
        const pendingUpdates = this.pendingUpdates;

        // The snapshot is a RECENT+FORWARD WINDOW (date >= cutoff, see initializeFirebaseSync) —
        // NOT the entire collection (loading all ~18.8k docs after the historical import hung boot).
        // A LOCAL appointment absent from the snapshot was either DELETED (maybe by another tab) OR
        // aged out of the window; either way we evict it from the live cache — Firestore still has it,
        // and Reports query history directly. Keep it only if it's a fresh optimistic create the
        // snapshot hasn't caught up to yet; else drop it.
        const nowMs = Date.now();
        // Safety: if the snapshot is empty (offline / transient error), NEVER drop local appointments.
        const snapshotHasData = firebaseAppointments.length > 0;
        const mergedAppointments = this.appointments.map(apt => {
          if (firebaseMap.has(apt.id)) {
            const fbApt = firebaseMap.get(apt.id)!;
            firebaseMap.delete(apt.id);

            // Check for pending update
            if (pendingUpdates.has(apt.id)) {
              const pending = pendingUpdates.get(apt.id)!;
              // Keep any locally-changed field until Firebase confirms it — protects
              // ALL updates (barberId, duration, status…), not just status, from the
              // snapshot race that used to revert reassigns and duration changes.
              const stillPending: Record<string, any> = {};
              for (const [field, expected] of Object.entries(pending)) {
                if ((fbApt as any)[field] !== expected) stillPending[field] = expected;
              }
              if (Object.keys(stillPending).length === 0) {
                pendingUpdates.delete(apt.id);
                return fbApt;
              }
              pendingUpdates.set(apt.id, stillPending);
              return { ...fbApt, ...stillPending } as Appointment;
            }

            return fbApt;
          }
          // Local-only → keep ONLY if a fresh optimistic create (has a pending write, or <30s old).
          // Otherwise it was deleted (here or in another tab) → drop it so it can't resurrect.
          const createdMs = (apt as any).createdAt instanceof Date
            ? (apt as any).createdAt.getTime()
            : new Date((apt as any).createdAt || 0).getTime();
          const freshOptimistic = pendingUpdates.has(apt.id) || (Number.isFinite(createdMs) && nowMs - createdMs < 30000);
          // ON-DEMAND PAST (Jose 2026-10-23): an appt dated BEFORE the live window was deliberately fetched
          // by ensureDateRange() and is NOT in the (recent+forward) snapshot — keep it, don't evict it as
          // "aged out". Only live-window appts absent from the snapshot are real deletes.
          const beforeWindow = !!this.liveCutoff && (apt.date || '') < this.liveCutoff;
          return (freshOptimistic || beforeWindow || !snapshotHasData) ? apt : null;
        }).filter(Boolean) as Appointment[];

        // Add any NEW appointments that are only in Firebase — EXCEPT ones the user just deleted
        // (tombstoned). Without this guard a delete that's still in-flight gets resurrected here.
        firebaseMap.forEach((fbApt) => {
          if (this.deletedIds.has(fbApt.id)) return; // still deleting — don't bring it back
          mergedAppointments.push(fbApt);
        });

        // Prune tombstones Firebase has confirmed are gone (id no longer present in the snapshot).
        if (this.deletedIds.size > 0) {
          const liveIds = new Set(firebaseAppointments.map(a => a.id));
          this.deletedIds.forEach(delId => { if (!liveIds.has(delId)) this.deletedIds.delete(delId); });
        }

        this.appointments = mergedAppointments;

        this.saveToLocalStorage();
        this.notifyListeners();
      }, (err) => {
        // ERROR CALLBACK (Jose 2026-10-29 audit): never let a denied/transient listen failure die silently.
        // A pre-auth permission-denied here used to leave appointments empty with no trace; the auth
        // re-subscribe (constructor) re-runs this once the user signs in. We do NOT wipe the local cache.
        console.warn('[appointmentService] appointments snapshot error (will retry on auth):', (err as any)?.message || err);
      });
    } catch (error) {
      console.error('Firebase sync initialization error:', error);
      // Fall back to local storage if Firebase fails
    }
  }

  /**
   * ON-DEMAND PAST FETCH (Jose 2026-10-23, load-the-minimum). The live subscription holds only
   * [liveCutoff, +∞). When the calendar navigates to an OLDER day, call this with that day's start — it
   * pulls [startYmd, earliestLoaded), merges + de-dupes into the cache, and extends earliestLoaded so we
   * never re-fetch. Future is always live, so forward navigation never needs this. No-op if already covered.
   */
  async ensureDateRange(startYmd: string): Promise<void> {
    if (!startYmd || !this.earliestLoaded || startYmd >= this.earliestLoaded) return; // already loaded
    if (this.inflightRangeFetch) { await this.inflightRangeFetch; if (startYmd >= this.earliestLoaded) return; }
    const run = (async () => {
      const target = this.earliestLoaded; // fetch the gap [startYmd, target)
      try {
        const { db } = await import('../config/firebase');
        const { collection, getDocs, where, orderBy } = await import('firebase/firestore');
        const { scopedQuery } = await import('./tenantQuery');
        const ref = collection(db, 'appointments');
        const snap = await getDocs(scopedQuery(ref, where('date', '>=', startYmd), where('date', '<', target), orderBy('date', 'asc')));
        const have = new Set(this.appointments.map(a => a.id));
        let added = 0;
        snap.forEach(d => {
          if (have.has(d.id)) return;
          const data: any = d.data();
          this.appointments.push({
            ...data, id: d.id,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            checkedInAt: data.checkedInAt?.toDate?.(),
            startedAt: data.startedAt?.toDate?.(),
            completedAt: data.completedAt?.toDate?.(),
          } as Appointment);
          added++;
        });
        this.earliestLoaded = startYmd;
        if (added > 0) { this.saveToLocalStorage(); this.notifyListeners(); }
        console.log(`%c[SLIK on-demand]%c fetched ${added} older appt(s) back to ${startYmd}`, 'background:#0ea5e9;color:#fff;padding:1px 5px;border-radius:3px', 'color:inherit');
      } catch (e) { console.warn('ensureDateRange failed (non-fatal — older day may show empty):', e); }
    })();
    this.inflightRangeFetch = run.finally(() => { this.inflightRangeFetch = null; });
    await this.inflightRangeFetch;
  }

  // Initialize without sample data - production ready
  private initializeSampleData() {
    // No sample data in production - all appointments come from Firebase
    // Clean up any test data that might exist
    this.cleanupTestData();
  }

  // NEUTERED (Jose 2026-10-24): this used to strip from the cache any appointment whose clientName
  // CONTAINED 'Test Client'/'John Doe'/'Jane Smith'/'Mike Wilson'/'Emily Johnson'/'Sample Client'/
  // 'Demo User' — the same landmine as the removed CalendarView mount-effect. A real client named
  // "Mike Wilson" (or an owner's "Test Client" booking) would get silently dropped from the local
  // view. Never filter real appointment data by name; there is no sample data in production anyway.
  private cleanupTestData() {
    /* no-op — all appointments come from Firestore; do not name-filter live data */
  }

  // Subscribe to changes
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Notify all listeners of changes
  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  // Get all appointments
  getAllAppointments(): Appointment[] {
    // IDENTITY-DEDUPE VIEW (Jose 2026-08-18): two entries with the same client+barber+date+time+service
    // (a stale cache copy or a rare double-write with different ids) collapse to ONE so every surface —
    // calendar, Link picker, reports — agrees. The calendar was already hiding it by stacking the two
    // identical blocks; the picker listed both. Prefer a real Firestore id over a temp optimistic one.
    const seen = new Map<string, Appointment>();
    for (const apt of this.appointments) {
      const key = `${(apt as any).clientId || apt.clientName || ''}|${apt.barberId || ''}|${apt.date || ''}|${apt.time || ''}|${apt.service || ''}`;
      const prev = seen.get(key);
      if (!prev) { seen.set(key, apt); continue; }
      const prevTemp = String(prev.id || '').startsWith('apt-');
      const curTemp = String(apt.id || '').startsWith('apt-');
      if (prevTemp && !curTemp) seen.set(key, apt); // keep the real-id record over a temp optimistic one
    }
    return Array.from(seen.values());
  }

  // RAW list — every stored appointment, NO identity-dedupe collapse. Reconciliation (e.g. the Fresha
  // Appointment-Status sync) MUST use this: two genuine Fresha bookings for the same client+barber+slot
  // with DIFFERENT statuses (one Completed, one Cancelled) share the dedupe key, so getAllAppointments()
  // hides one — and the sync could never satisfy both file rows, oscillating forever ("stuck at 1").
  // Matching by each doc's own freshaRef against the raw set fixes that. (Jose 2026-10-11)
  getAllAppointmentsRaw(): Appointment[] {
    return [...this.appointments];
  }

  // Get appointments for a specific date
  getAppointmentsByDate(date: string): Appointment[] {
    return this.appointments.filter(apt => apt.date === date);
  }

  // Get appointments for a specific barber
  getAppointmentsByBarber(barberId: string): Appointment[] {
    return this.appointments.filter(apt => apt.barberId === barberId);
  }

  // Get today's appointments
  getTodaysAppointments(): Appointment[] {
    const today = ymd();
    return this.getAppointmentsByDate(today);
  }

  // Get upcoming appointments (next 7 days)
  getUpcomingAppointments(): Appointment[] {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    return this.appointments.filter(apt => {
      const aptDate = parseYmd(apt.date); // LOCAL date (bare `new Date(ymd)` = UTC midnight = prev evening in ET → off-by-one-day)
      return aptDate >= today && aptDate <= nextWeek;
    });
  }

  // 📍 MULTI-LOCATION CONFLICT CHECKING
  // Checks if a barber has ANY appointment (at any location) that conflicts with the given time
  // This prevents double-booking staff like Kelly who work at multiple locations
  hasConflictAcrossLocations(
    barberId: string,
    date: string,
    time: string,
    duration: number,
    excludeAppointmentId?: string // Exclude this appointment when editing
  ): { hasConflict: boolean; conflictingAppointment?: Appointment } {
    // Convert time to minutes — use the SHARED parser so both "10:00" and "10:00 AM" work.
    // (naive split(':') gave NaN for 12-hour strings, making the overlap test true for EVERY
    // existing appointment → phantom "already booked" on any slot. Availability tab was fine
    // because it already uses parseTimeToMinutes.) BEHAVIOR-PIN: SLIK booking phantom conflict.
    const requestedStart = parseTimeToMinutes(time);
    if (requestedStart == null) return { hasConflict: false };
    const requestedEnd = requestedStart + duration;

    // Find any conflicting appointment for this barber on this date (ANY location)
    const conflict = this.appointments.find(apt => {
      // Skip cancelled/completed appointments
      if (apt.status === 'cancelled' || apt.status === 'completed' || apt.status === 'no-show') {
        return false;
      }

      // Skip the appointment being edited
      if (excludeAppointmentId && apt.id === excludeAppointmentId) {
        return false;
      }

      // Must be same barber and same date
      if (apt.barberId !== barberId || apt.date !== date) {
        return false;
      }

      // Check time overlap (regardless of location) — same robust parser.
      const aptStart = parseTimeToMinutes(apt.time);
      if (aptStart == null) return false;
      const aptEnd = aptStart + (apt.duration || 30);

      // End-exclusive overlap: back-to-back (10:00–10:30 vs 10:30–11:00) does NOT conflict.
      const hasOverlap = !(requestedEnd <= aptStart || requestedStart >= aptEnd);

      return hasOverlap;
    });

    if (conflict) {
      return {
        hasConflict: true,
        conflictingAppointment: conflict
      };
    }

    return { hasConflict: false };
  }

  // Get appointments by location
  getAppointmentsByLocation(locationId: string): Appointment[] {
    return this.appointments.filter(apt => apt.locationId === locationId);
  }

  // Get appointments by date and location
  getAppointmentsByDateAndLocation(date: string, locationId: string): Appointment[] {
    return this.appointments.filter(apt => apt.date === date && apt.locationId === locationId);
  }

  // Get appointments for a specific client (sorted by date, most recent first).
  // NOTE: reads the IN-MEMORY cache, which only holds the loaded date WINDOW — a client's full past/future
  // may not be present. For a profile/appointment-card that must show the REAL visit count + full upcoming
  // list, use fetchAppointmentsByClient() (direct Firestore read) instead. (Jose 2026-10-29)
  getAppointmentsByClient(clientId: string): Appointment[] {
    return this.appointments
      .filter(apt => apt.clientId === clientId)
      .sort((a, b) => {
        // Sort by date descending (most recent first). YMD strings compare chronologically as-is — no
        // Date parsing (the old `new Date(a.date + ' ' + time)` broke on 12-hour times + UTC-shifted days).
        if (a.date !== b.date) return (b.date || '').localeCompare(a.date || '');
        return (parseTimeToMinutes(b.time) ?? 0) - (parseTimeToMinutes(a.time) ?? 0);
      });
  }

  /** RELIABLE per-client read straight from Firestore (ALL of a client's appointments, any date), not the
   *  loaded window. Powers the real visit count + full upcoming list on the profile + appointment card so
   *  they don't read 0 just because the client's history/future isn't in the cache. (Jose 2026-10-29) */
  async fetchAppointmentsByClient(clientId: string): Promise<Appointment[]> {
    if (!clientId) return [];
    try {
      const { collection, getDocs, query, where } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const snap = await getDocs(query(collection(db, 'appointments'), where('clientId', '==', clientId)));
      const rows = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Appointment[];
      return rows.sort((a, b) => {
        if (a.date !== b.date) return (b.date || '').localeCompare(a.date || '');
        return (parseTimeToMinutes(b.time) ?? 0) - (parseTimeToMinutes(a.time) ?? 0);
      });
    } catch (e) {
      console.warn('fetchAppointmentsByClient failed (falling back to cache):', e);
      return this.getAppointmentsByClient(clientId);
    }
  }

  /**
   * Validate that an appointment falls within business hours and barber availability.
   * Throws a descriptive error if validation fails.
   * Skips validation for emergency bookings (source='emergency' or emergencyBooking=true).
   */
  private async validateAppointmentTime(appointment: Omit<Appointment, 'id' | 'createdAt'>): Promise<void> {
    // Skip validation for emergency bookings — they override normal rules. ALSO skip an already-
    // COMPLETED sale (Jose 2026-08-30): a Register walk-in/POS checkout is created with status
    // 'completed' AFTER the money moved — it's a RETROACTIVE record, so business-hours / barber-
    // availability checks are moot and MUST NEVER block it (throwing here silently lost after-hours
    // walk-in sales → charge with no record). Mirrors the completed-sale exemption on the conflict guard.
    if (appointment.source === 'emergency' || appointment.emergencyBooking || (appointment as any).status === 'completed') {
      return;
    }

    const appointmentDate = new Date(appointment.date + 'T00:00:00');
    const dayName = appointmentDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as keyof BusinessHours;

    // --- Business hours validation ---
    try {
      const settings = await shopSettingsService.getSettings();
      // A holiday / special-date override (Settings → Special Hours) WINS over the weekly hours, so a
      // day closed for a holiday blocks staff booking just like it blocks online booking. (Jose 2026-10-23)
      const dayHours: any = resolveDayHours(appointmentDate, settings.businessHours[dayName], settings.specialHours) || settings.businessHours[dayName];

      if (dayHours.closed) {
        const special = settings.specialHours?.[appointment.date];
        throw new Error(special?.note
          ? `Shop is closed that day (${special.note})`
          : `Outside business hours: shop is closed on ${appointment.date}`);
      }

      const [aptHour, aptMin] = appointment.time.split(':').map(Number);
      const aptMinutes = aptHour * 60 + aptMin;
      const aptEndMinutes = aptMinutes + (appointment.duration || 30);

      const [openH, openM] = dayHours.open.split(':').map(Number);
      const [closeH, closeM] = dayHours.close.split(':').map(Number);
      const openMinutes = openH * 60 + openM;
      const closeMinutes = closeH * 60 + closeM;

      if (aptMinutes < openMinutes) {
        throw new Error(`Outside business hours: shop opens at ${dayHours.open}`);
      }

      if (aptEndMinutes > closeMinutes) {
        const closeFormatted = this.formatTimeForDisplay(dayHours.close);
        throw new Error(`Outside business hours: shop closes at ${closeFormatted}`);
      }
    } catch (error) {
      // Re-throw validation errors, swallow settings fetch failures
      if (error instanceof Error && error.message.startsWith('Outside business hours')) {
        throw error;
      }
      console.warn('Could not validate business hours:', error);
    }

    // --- Barber availability validation ---
    if (appointment.barberId && appointment.barberId !== 'any') {
      const rawBarber = barberDataService.getBarberById(appointment.barberId);
      // Fold any per-date "Edit Shift" override onto the barber so the WRITE-PATH validator honors a
      // changed shift exactly like the calendar/booking display does (ONE availability brain). Without this,
      // a barber who edited her shift to work a normally-off day was still rejected here ("not available on
      // Xs") even though every display said she was open. UTC-safe: appointmentDate is the LOCAL day and
      // applyShift keys off date.getDay(); shift hours are wall-clock strings (no Date/UTC conversion).
      // (Jose 2026-10-29 — "the set availability / edit shift needs to actually work")
      const shift = shiftService.getShift(appointment.barberId, appointment.date);
      const barber = applyShift(rawBarber as any, appointmentDate, shift as any) as typeof rawBarber;

      if (barber) {
        const barberDay = barber.availability[dayName as keyof typeof barber.availability];

        // Canonical "is working?" read (enabled is canonical; available is the legacy mirror). Using the
        // shared isDayEnabled keeps this in lockstep with every display surface AND with the folded shift.
        if (!isDayEnabled(barberDay as any)) {
          const dayDisplay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
          throw new Error(`${barber.firstName} is not available on ${dayDisplay}s`);
        }

        // Check barber's working hours for that day
        if (barberDay.start && barberDay.end) {
          const [aptHour, aptMin] = appointment.time.split(':').map(Number);
          const aptMinutes = aptHour * 60 + aptMin;
          const aptEndMinutes = aptMinutes + (appointment.duration || 30);

          const [bStartH, bStartM] = barberDay.start.split(':').map(Number);
          const [bEndH, bEndM] = barberDay.end.split(':').map(Number);
          const barberStartMinutes = bStartH * 60 + bStartM;
          const barberEndMinutes = bEndH * 60 + bEndM;

          if (aptMinutes < barberStartMinutes || aptEndMinutes > barberEndMinutes) {
            throw new Error(
              `${barber.firstName} works ${barberDay.start} - ${barberDay.end} on ${dayName}s`
            );
          }
        }
      }
    }
  }

  /**
   * Format a 24h time string (e.g. "20:00") for display (e.g. "8 PM")
   */
  private formatTimeForDisplay(time: string): string {
    const [h, m] = time.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m === 0 ? `${displayHour} ${period}` : `${displayHour}:${String(m).padStart(2, '0')} ${period}`;
  }

  // Add new appointment with Firebase sync
  // Includes multi-location conflict checking to prevent double-booking.
  // PUBLIC ENTRY = idempotency wrapper: two identical creates fired at once (double-click) share ONE
  // write. The identity is client+barber+date+time+service; emergencies (intentional overlaps) skip it.
  // opts.allowOverlap (Jose 2026-10-21): a MANAGER squeeze deliberately double-books a taken slot — skip the
  // conflict guard just like emergency/drag. Part of the "squeeze everywhere" consolidation (MANAGER-SQUEEZE-
  // EVERYWHERE-SPEC); never exposed to clients.
  async addAppointment(appointment: Omit<Appointment, 'id' | 'createdAt'>, opts?: { allowOverlap?: boolean; overrideAvailability?: boolean; background?: boolean }): Promise<Appointment> {
    const a = appointment as any;
    // 🧾 WHO CREATED IT (Jose 2026-10-29): stamp the tapped-in operator (PIN'd barber at the station) so the
    // appointment card can answer "who booked this?" — not just WHEN (createdAt) + HOW (source). Staff paths
    // run with an operator session; online/import/portal have none, so we leave it blank there (source says
    // Customer app / Imported). Authoritative audit trail: who · when · where/how. (ties to task #150)
    if (!a.createdByName) {
      try {
        const opId = getOperatorBarberId();
        if (opId) {
          const b = barberDataService.getBarberById(opId);
          a.createdByBarberId = opId;
          a.createdByName = b ? (b.firstName || (b as any).name || '') : '';
        }
      } catch { /* non-fatal */ }
    }
    const isEmergency = a.source === 'emergency' || a.emergencyBooking === true;
    const idKey = !isEmergency && (a.clientId || a.clientName) && a.barberId && a.date && a.time
      ? `${a.clientId || a.clientName}|${a.barberId}|${a.date}|${a.time}|${a.service || ''}`
      : null;
    if (idKey) {
      const inflight = this.inflightCreates.get(idKey);
      if (inflight) {
        console.warn(`[SLIK dedupe] concurrent identical create suppressed → ${idKey}`);
        return inflight; // same booking already being written — return that one, don't duplicate
      }
      const run = this._addAppointmentInner(appointment, opts);
      this.inflightCreates.set(idKey, run);
      run.finally(() => this.inflightCreates.delete(idKey)).catch(() => {});
      return run;
    }
    return this._addAppointmentInner(appointment, opts);
  }

  private async _addAppointmentInner(appointment: Omit<Appointment, 'id' | 'createdAt'>, opts?: { allowOverlap?: boolean; overrideAvailability?: boolean; background?: boolean }): Promise<Appointment> {
    // 🕐 VALIDATE business hours + barber availability (skips emergency bookings).
    // MANAGER OVERRIDE (Jose 2026-10-29): a manager/owner may deliberately book OUTSIDE a barber's schedule
    // or business hours — e.g. add a client on Kelly's day off, or after close. The staff calendar passes
    // overrideAvailability for a privileged operator; skipping the validator here is what lets that write
    // land instead of dying with "<barber> is not available on Xs" (surfaced as "failed to upload"). The
    // conflict / double-book guard below STILL runs (needs allowOverlap too to intentionally double-book).
    if (!opts?.overrideAvailability) {
      await this.validateAppointmentTime(appointment);
    }

    // 🚫 DOUBLE-BOOK GUARD (2026-08-05): a barber's taken slot may be overlapped ONLY by
    // (1) an EMERGENCY booking, (2) a manual drag, or (3) a manual barber reassign. Drag +
    // reassign go through updateAppointment (NOT this method), so they're already exempt.
    // Every normal path — POS booking, walk-in, customer/public/portal when routed here — is
    // BLOCKED. Emergency is flagged by source==='emergency' / emergencyBooking===true.
    // BEHAVIOR-PIN: SLIK no double-booking except emergency + manual.
    // Skip booking-time guards for emergency AND for an already-COMPLETED sale: the Register's
    // walk-in/product/gift-card checkout creates the appointment with status='completed' AFTER money
    // moved — it's a retroactive record, so a "double-book" conflict/no-show/new-client gate is moot
    // and must never block it. (Jose 2026-08-13, register unify) The Firestore write below still runs.
    const _isEmergencyBooking = (appointment as any).source === 'emergency' || (appointment as any).emergencyBooking === true || (appointment as any).status === 'completed';
    const _bypassConflict = _isEmergencyBooking || opts?.allowOverlap === true; // ⚡ manager squeeze
    // Stamp a manager squeeze so it OVERLAPS ON PURPOSE everywhere — the server double-book referee
    // (enforceNoDoubleBook CF) + every guard leave it alone, so it's not auto-cancelled right after it's
    // created (Jose 2026-10-21: squeezed appt got a confirm then an instant auto-cancel). (MANAGER-SQUEEZE spec)
    if (opts?.allowOverlap === true) {
      (appointment as any).squeezedIn = true;
      // SQUEEZE DEFAULT (Jose 2026-10-21): a manager-override squeeze is a quick 15-min "Squēz" — its OWN
      // service so it reads instantly on the calendar + lands as a distinct line in reports (same 15-min
      // idea as an emergency, but its own category), not a silent 30-min appointment. The operator can
      // extend/relabel it on the card after. A caller can opt out with squeezeKeepService:true.
      if ((appointment as any).squeezeKeepService !== true) {
        appointment.service = 'Squēz';
        (appointment as any).serviceName = 'Squēz';
        appointment.duration = 15;
        (appointment as any).serviceCategory = 'Squeeze';
      }
    }
    if (!_bypassConflict && appointment.barberId && appointment.barberId !== 'any') {
      const conflict = this.hasConflictAcrossLocations(
        appointment.barberId,
        appointment.date,
        appointment.time,
        appointment.duration || 30
      );

      if (conflict.hasConflict && conflict.conflictingAppointment) {
        const conflictApt = conflict.conflictingAppointment;
        throw new Error(
          `Conflict: ${appointment.barberName || 'This barber'} already has ${conflictApt.clientName || 'an appointment'} at ${conflictApt.time}. Pick another time or barber.`
        );
      }
    }

    // 🧑 ENSURE A REAL CLIENT PROFILE (2026-08-07, punch #3): every booking should resolve to — or
    // create — a real client record so reporting, the no-show block (stored ON the client) and history
    // have a home. No more guest_/walk-in-/temp-/client-<ts> orphans or duplicate people. Dedup via the
    // shared identity brain (phone → email → name). Truly anonymous walk-ins (no identity) keep their
    // placeholder id. Runs BEFORE the block + new-client checks so those operate on the real clientId.
    if (!_isEmergencyBooking) {
      try {
        const cid = String((appointment as any).clientId || '');
        const isPlaceholder = !cid || /^(walk-in|temp-|guest|client-)/i.test(cid);
        const nm = (appointment.clientName || '').trim();
        const realName = !!nm && !/^walk-in/i.test(nm) && nm.toLowerCase() !== 'guest';
        const hasIdentity = normalizePhone(appointment.clientPhone).length >= 7 || realName;
        if (isPlaceholder && hasIdentity) {
          const who = { phone: appointment.clientPhone, email: (appointment as any).clientEmail, name: nm };
          const existing: any = findSamePerson(who, clientDataService.getAllClients() as any);
          if (existing?.id) {
            (appointment as any).clientId = existing.id; // dedup: link to the real person
          } else {
            const parts = nm.split(/\s+/);
            const created = await clientDataService.addClient({
              firstName: parts[0] || 'Client',
              lastName: parts.slice(1).join(' ') || '',
              phone: appointment.clientPhone || '',
              email: (appointment as any).clientEmail || '',
              membershipType: 'regular',
              joinDate: new Date(),
              totalVisits: 0, totalSpent: 0, averageTicket: 0, rebookingRate: 0,
              noShowCount: 0, cancellationCount: 0, tags: [], status: 'active', loyaltyPoints: 0,
            } as any);
            if (created?.id) (appointment as any).clientId = created.id;
          }
        }
      } catch (e) { console.warn('Ensure-client-profile failed (non-fatal):', e); }
    }

    // 🚫 NO-SHOW BLOCK enforcement at the POS (2026-08-07): a blocked client (requirePaymentUpfront)
    // cannot be booked without a signed prepay agreement — throw NO_SHOW_BLOCK so the booking UI can
    // show the agreement modal (and the calendar can't silently create it). Emergency bookings exempt.
    if (!_isEmergencyBooking && !(appointment as any).prepaidAgreement) {
      try {
        const bDigits = (appointment.clientPhone || '').replace(/\D/g, '');
        const bCid = (appointment as any).clientId ? String((appointment as any).clientId) : '';
        let blockClient: any = bCid ? clientDataService.getClientById(bCid) : null;
        if (!blockClient && bDigits.length >= 7) {
          const byPhone = clientDataService.getClientsByPhone(bDigits);
          blockClient = byPhone && byPhone[0];
        }
        const bps = blockClient?.paymentSettings;
        if (bps?.requirePaymentUpfront === true) {
          const err: any = new Error(bps.noShowNotice || DEFAULT_NO_SHOW_NOTICE);
          err.code = 'NO_SHOW_BLOCK';
          throw err;
        }
      } catch (e: any) {
        if (e?.code === 'NO_SHOW_BLOCK') throw e;
        console.warn('No-show block check failed (non-fatal):', e);
      }
    }

    // 🆕 NEW-CLIENT (FIXED 2026-08-07): a first-timer = NO PRIOR APPOINTMENT for this client (matched
    // by real clientId or normalized phone). A RETURNING client is NOT new even with 0 completed visits
    // (e.g. they no-showed) — the old visits===0 rule wrongly re-tagged no-show clients as "new" on
    // every booking. This is AUTHORITATIVE (overrides any value the UI passed) so every POS path agrees.
    // The new appointment isn't in this.appointments yet, so this only sees PRIOR bookings. 45-min min
    // for a true first-timer. Emergency bookings are exempt.
    if (!_isEmergencyBooking) {
      try {
        // Canonical last-10 phone (normalizePhone) so "+1 973…" matches a stored 10-digit record —
        // otherwise a returning client reads as NEW → 45-min window → overlap. (Jose 2026-10-21)
        const digits = normalizePhone(appointment.clientPhone);
        const cid = (appointment as any).clientId ? String((appointment as any).clientId) : '';
        const realCid = !!cid && !cid.startsWith('walk-in') && !cid.startsWith('temp-') && !cid.startsWith('guest');
        const nameKey = (appointment.clientName || '').trim().toLowerCase();
        const isPlaceholder = !nameKey || nameKey.startsWith('walk-in') || nameKey === 'guest';
        const hasIdentity = digits.length >= 7 || (!isPlaceholder && !!nameKey);

        const hasPrior = this.appointments.some((a) => {
          if (realCid && (a as any).clientId && String((a as any).clientId) === cid) return true;
          if (digits.length >= 7) { const ad = normalizePhone(a.clientPhone); if (ad && ad === digits) return true; }
          return false;
        });

        // RELIABLE RETURNING SIGNAL (Jose 2026-10-29): this.appointments is only the loaded WINDOW, so a real
        // returning client (Jake Crane, 98 visits) whose history isn't in memory read as NEW. A linked client
        // record carries lifetime totals even with zero appointments loaded — trust it: any prior visits /
        // spend / lastVisit = NOT a first-timer.
        let knownReturning = false;
        try {
          const rec: any = realCid
            ? clientDataService.getClientById(cid)
            : (digits.length >= 7 ? (clientDataService.getClientsByPhone(digits) || [])[0] : null);
          if (rec && ((rec.totalVisits || 0) > 0 || !!rec.lastVisit || (rec.totalSpent || 0) > 0)) knownReturning = true;
        } catch { /* non-fatal */ }

        const isNew = hasIdentity && !hasPrior && !knownReturning;
        (appointment as any).isNewClient = isNew;
        // PER-SHOP new-client minimum (BookingRules.newClientMinDuration) — default 45. (2026-08-08)
        // HONOR THE OPERATOR'S CHOSEN DURATION (Jose 2026-10-20, "little John Robert" phantom): on the
        // staff calendar the barber explicitly picks the service length. SILENTLY inflating a 30-min
        // kid's cut up to the 45-min new-client minimum pushed the end time into the next appointment
        // and tripped the authoritative double-book guard ("that slot was just taken") — e.g. a 2:30
        // booking failed because 2:30 + 45 = 3:15 collided with the existing 3:00. The in-memory guard
        // ran BEFORE this bump (30 min = clear) and the fresh-read guard ran AFTER (45 min = conflict),
        // so the slot looked free then refused to save. The minimum is now only a FALLBACK when NO
        // duration was chosen; an explicit duration is never overridden. (First-timer protection for
        // self-service bookings lives in the customer-portal / public-booking slot generation — those
        // are separate apps and never call this staff-only path, so this does not weaken it.)
        if (isNew && !(appointment.duration && appointment.duration > 0)) {
          const rules = mergeBookingRules((await shopSettingsService.getSettings())?.bookingRules);
          appointment.duration = rules.newClientMinDuration;
        }
      } catch (e) {
        console.warn('New-client detection failed (non-fatal):', e);
      }
    }

    // Sync to Firebase FIRST to get the real ID
    try {
      const { db } = await import('../config/firebase');
      const { collection, getDocs, query, where, doc: mkDoc, setDoc } = await import('firebase/firestore');
      const appointmentsRef = collection(db, 'appointments');

      // 🚫 CROSS-SURFACE DOUBLE-BOOK GUARD (2026-08-08): re-check overlap against a FRESH Firestore
      // read RIGHT BEFORE writing. The local this.appointments cache can be stale if another surface
      // (a barber's phone, the customer portal, another logged-in POS screen) booked since this
      // surface last synced — that's what caused overlapping appointments across devices. This is the
      // authoritative guard; the earlier hasConflictAcrossLocations check is just fast local feedback.
      // Uses the SAME shared brain (hasApptConflict) as the customer apps; no-shows occupy per the
      // shop's rule. Emergency/manual overlaps stay exempt.
      // BUGFIX (Jose 2026-10-21): this authoritative fresh-read guard only exempted EMERGENCY, so a
      // MANAGER ⚡ squeeze (allowOverlap → _bypassConflict) passed the first guard (line ~639) then got
      // killed HERE with "Double-book prevented: that slot was just taken … on another device" — the exact
      // waitlist Book-directly failure. Honor the full bypass (emergency OR manager squeeze). #130
      if (!_bypassConflict && appointment.barberId && appointment.barberId !== 'any') {
        const reqStart = parseTimeToMinutes(appointment.time);
        if (reqStart != null) {
          // BOUNDED FRESH-READ GUARD (Jose 2026-10-24): on the shop iPad's stalled Firestore long-poll,
          // these getDocs could take 45-50s → that was Kelly's booking spinner. Race each read against a
          // short deadline and run them in PARALLEL. If the server is too slow, fall back to the LOCAL
          // conflict check (already passed above via hasConflictAcrossLocations) and proceed — the write is
          // backgrounded + idempotent. A healthy connection returns in <1s and stays fully authoritative.
          const GUARD_MS = 4000;
          const TO = Symbol('guard-timeout');
          const timed = <T>(p: Promise<T>): Promise<T | typeof TO> =>
            Promise.race([p.catch(() => TO as any), new Promise<typeof TO>((r) => setTimeout(() => r(TO), GUARD_MS))]);
          const [settingsR, freshSnapR, blkSnapR] = await Promise.all([
            timed(shopSettingsService.getSettings()),
            timed(getDocs(query(appointmentsRef, where('barberId', '==', appointment.barberId), where('date', '==', appointment.date)))),
            timed(getDocs(query(collection(db, 'blockedTimes'), where('barberId', '==', appointment.barberId), where('date', '==', appointment.date)))),
          ]);
          const rules = mergeBookingRules(settingsR === TO ? undefined : (settingsR as any)?.bookingRules);
          if (freshSnapR === TO) {
            // Server too slow / read failed → rely on the local pre-check (already passed) + proceed. Tag
            // it so a background reconcile could catch the rare simultaneous-two-device race.
            (appointment as any)._freshGuardTimedOut = true;
            console.warn('%c[SLIK conflict-guard]%c fresh read slow/unavailable — using local check, booking proceeds',
              'background:#b45309;color:#fff;padding:2px 6px;border-radius:4px;font-weight:bold', 'color:inherit');
          } else {
            const freshAppts = (freshSnapR as any).docs.map((d: any) => ({
              barberId: d.data().barberId, time: d.data().time, duration: d.data().duration, status: d.data().status,
            }));
            // ⛔ Barber time-blocks occupy exactly like an appointment (merge as active pseudo-appts). If the
            // blocks read timed out, skip it — never break booking on a slow/failed blocks read. (Jose 2026-08-12)
            if (blkSnapR !== TO) {
              for (const d of (blkSnapR as any).docs) {
                const b = d.data() as any;
                freshAppts.push({ barberId: b.barberId, time: b.startTime, duration: b.duration || 30, status: 'confirmed' });
              }
            }
            const c = hasApptConflict(appointment.barberId, reqStart, appointment.duration || 30, freshAppts, undefined, rules.noShowOccupiesSlot);
            console.log(`%c[SLIK conflict-guard]%c fresh re-check: ${appointment.barberName || appointment.barberId} @ ${appointment.time} on ${appointment.date} — ${freshAppts.length} live appt(s) — ${c.conflict ? '🚫 BLOCKED (overlaps ' + c.withTime + ')' : '✅ clear'}`,
              'background:#7c3aed;color:#fff;padding:2px 6px;border-radius:4px;font-weight:bold', 'color:inherit');
            if (c.conflict) {
              try { (await import('react-hot-toast')).default.error(`⛔ Double-book prevented: that slot was just taken at ${c.withTime} on another device.`, { duration: 6000 }); } catch { /* toast optional */ }
              throw new Error(`Conflict: ${appointment.barberName || 'This barber'} was just booked at ${c.withTime} (from another device). Pick another time or barber.`);
            }
          }
        }
      }

      // CSV-IMPORT TIGHTENING (2026-08-01): tag the appointment with its shop (multi-tenant).
      let shopId = (appointment as any).shopId || (this as any)._cachedShopId;
      if (!shopId) {
        try {
          const shopsSnap = await getDocs(collection(db, 'shops'));
          if (!shopsSnap.empty) { shopId = shopsSnap.docs[0].id; (this as any)._cachedShopId = shopId; }
        } catch { /* offline / no shop yet */ }
      }

      // The passed-all-guards write. doc() gives the real Firestore ID SYNCHRONOUSLY, so we can apply it
      // locally + return NOW and (in background mode) let the server write finish on its own — the
      // "Booking…" spinner never hangs on a stalled connection's write-ack. (Jose 2026-10-23, doctrine)
      // IDENTITY KEY (Jose 2026-10-30): stamp the canonical phoneKey (last-10) so the portal + any surface
      // can find this appt by the client's phone identity, not just clientId — the fix that lets imported
      // history surface under a signed-up client. See scripts/backfill-appointment-phonekey.cjs.
      const _pk = normalizePhone((appointment as any).clientPhone);
      const data = { ...appointment, ...(_pk.length === 10 ? { phoneKey: _pk } : {}), ...(shopId ? { shopId } : {}), createdAt: new Date() };
      const docRef = mkDoc(appointmentsRef);

      const newAppointment: Appointment = {
        ...appointment,
        id: docRef.id, // Firebase-generated ID (available before the write resolves)
        createdAt: new Date()
      };

      // Add to local state immediately (optimistic — visible on the calendar now)
      this.appointments.push(newAppointment);
      this.saveToLocalStorage();
      this.notifyListeners();

      if (opts?.background) {
        setDoc(docRef, data)
          .then(() => { this.lastWriteError = null; })
          .catch((e: any) => {
            this.lastWriteError = e?.code ? `${e.code}: ${e.message || ''}` : String(e?.message || e);
            console.error('[bg addAppointment] server write failed (stays optimistic; Firestore persistence retries):', e);
          });
        return newAppointment; // resolve the caller NOW
      }

      await setDoc(docRef, data);
      this.lastWriteError = null; // server write succeeded
      return newAppointment;
    } catch (error) {
      // Re-throw conflict errors
      if (error instanceof Error && error.message.includes('Conflict:')) {
        throw error;
      }

      const anyErr: any = error;
      this.lastWriteError = anyErr?.code ? `${anyErr.code}: ${anyErr.message || ''}` : String(anyErr?.message || error);
      console.error('Failed to sync appointment to Firebase:', error);

      // 🚫 GUARD-BYPASS FIX (Jose 2026-08-11): the fresh-read double-book guard above lives INSIDE this
      // try. If getDocs threw for ANY non-conflict reason (transient network / index / offline), we used
      // to fall straight through and create the appointment with NO conflict check → silent double-book.
      // Re-run the local-cache conflict check here before the offline fallback so we never book over a
      // slot we can already see is taken. (Emergency/manual overlaps stay exempt, same as the main path.)
      const _isEmergency = (appointment as any).source === 'emergency' || (appointment as any).emergencyBooking === true;
      if (!_isEmergency && appointment.barberId && appointment.barberId !== 'any') {
        const localConflict = this.hasConflictAcrossLocations(
          appointment.barberId,
          appointment.date,
          appointment.time,
          appointment.duration || 30
        );
        if (localConflict.hasConflict && localConflict.conflictingAppointment) {
          throw new Error(
            `Conflict: ${appointment.barberName || 'This barber'} already has ${localConflict.conflictingAppointment.clientName || 'an appointment'} at ${localConflict.conflictingAppointment.time}. Pick another time or barber.`
          );
        }
      }

      // Fallback to local-only if Firebase fails
      const newAppointment: Appointment = {
        ...appointment,
        id: `apt-${Date.now()}`,
        createdAt: new Date()
      };

      this.appointments.push(newAppointment);
      this.saveToLocalStorage();
      this.notifyListeners();

      return newAppointment;
    }
  }

  // Alias for backward compatibility
  async createAppointment(appointment: Omit<Appointment, 'id' | 'createdAt'>): Promise<Appointment> {
    return this.addAppointment(appointment);
  }

  /**
   * IMPORT CREATE-BY-REF (Jose 2026-09-12): create a MISSING appointment from the Fresha Appointment-list
   * import at the DETERMINISTIC id `fresha_<ref>` — idempotent (re-drop overwrites the same doc, never a
   * dup) + ref-matchable by the status importer. NON-DESTRUCTIVE via setDoc(merge) — fills the doc, never
   * blanks. Stamps shopId (multi-tenant) + source:'import'. No double-book guard: these are historical
   * Fresha bookings, not live slots. onSnapshot folds it into the cache; we also reflect locally so the
   * drop's result is instant. BEHAVIOR-PIN: freshaApptStatusImport + production-safety-net-2026-09-11.
   */
  async createImportedAppointment(
    a: Omit<Appointment, 'id' | 'createdAt'> & { freshaRef: string },
  ): Promise<Appointment> {
    const { db } = await import('../config/firebase');
    const { doc, setDoc, getDocs, collection } = await import('firebase/firestore');
    let shopId = (a as any).shopId || (this as any)._cachedShopId;
    if (!shopId) {
      try {
        const s = await getDocs(collection(db, 'shops'));
        if (!s.empty) { shopId = s.docs[0].id; (this as any)._cachedShopId = shopId; }
      } catch { /* offline / no shop yet */ }
    }
    const id = `fresha_${a.freshaRef}`;
    const _pk = normalizePhone((a as any).clientPhone);
    await setDoc(doc(db, 'appointments', id), {
      ...a,
      ...(_pk.length === 10 ? { phoneKey: _pk } : {}), // IDENTITY KEY — imports findable by phone (SLEK + future shops)
      ...(shopId ? { shopId } : {}),
      source: (a as any).source || 'import',
      createdAt: new Date(),
    }, { merge: true });

    const created: Appointment = { ...(a as any), id, createdAt: new Date() } as Appointment;
    const idx = this.appointments.findIndex((x) => x.id === id);
    if (idx >= 0) this.appointments[idx] = { ...this.appointments[idx], ...created };
    else this.appointments.push(created);
    this.saveToLocalStorage();
    this.notifyListeners();
    return created;
  }

  /**
   * IMPORT UPSERT (Jose 2026-09-11): re-importing appointments must NEVER duplicate or delete — same as
   * the financial-history importer. Matches an existing appointment by business key (client+barber+date+
   * time) in the live cache; if found, UPDATES it with the incoming non-empty fields (fills in tips /
   * price / status / service / notes / products) without blanking anything; else creates it. Idempotent
   * (re-dropping the same file is a no-op) + additive (new rows added). Live booking (addAppointment) is
   * untouched. BEHAVIOR-PIN: project-slik-production-safety-net-2026-09-11.
   */
  async upsertImportedAppointment(
    appointment: Omit<Appointment, 'id' | 'createdAt'>,
  ): Promise<{ action: 'created' | 'updated'; appointment: Appointment | null }> {
    const key = appointmentDedupeKey(appointment as any);
    // Only dedupe when the key is fully qualified (client + barber + date + time); else always create.
    const qualified = key.split('|').every((p) => p.length > 0);
    const existing = qualified ? this.appointments.find((a) => appointmentDedupeKey(a as any) === key) : undefined;
    if (existing) {
      const src = appointment as any;
      const IMPORTABLE = ['service', 'duration', 'price', 'status', 'notes', 'tipAmount', 'discountAmount',
        'emergencyFee', 'productsSold', 'paymentMethod', 'clientId', 'clientName', 'clientPhone',
        'barberId', 'barberName'] as const;
      const upd: Record<string, any> = {};
      for (const f of IMPORTABLE) { const v = src[f]; if (v !== undefined && v !== null && v !== '') upd[f] = v; }
      const updated = await this.updateAppointment(existing.id, upd as Partial<Appointment>);
      return { action: 'updated', appointment: updated };
    }
    const created = await this.addAppointment(appointment);
    return { action: 'created', appointment: created };
  }

  // 🔁 SHARED RESCHEDULE BRAIN (Jose 2026-08-11): reschedule an appointment — and, when applyToSeries
  // is true and it's part of a recurring series, move THIS visit + every FUTURE sibling to the new
  // weekday/time/barber (all shift by the same weekday delta so the interval is preserved; past/
  // completed/no-show + earlier siblings untouched). ONE brain used by the calendar, the in-house
  // client profile, and (via shared/) the customer portal — so "manage from one place" can't drift.
  // `time` may be "3:00 PM" (12h) or "15:00" (24h). Returns the number moved + the new weekday label.
  /**
   * Fresh-read double-book check for a MOVE (reschedule / drag-drop). Mirrors the write-time guard in
   * addAppointment (2026-08-08): reads LIVE Firestore appointments + blockedTimes for (barberId, date)
   * and runs the shared hasApptConflict, ignoring the appointment being moved. Fail-OPEN on a read error
   * (never block a legit move on a transient Firestore failure). Returns { conflict, withTime? }.
   * (Jose 2026-10-03 — extend the fresh-read doctrine to the move/reschedule paths, not just creates.)
   */
  async checkMoveConflictFresh(
    barberId: string,
    dateStr: string,       // yyyy-MM-dd
    timeHHMM: string,      // "HH:MM" 24h
    duration: number,
    ignoreApptId?: string,
  ): Promise<{ conflict: boolean; withTime?: string }> {
    if (!barberId || barberId === 'any' || barberId === '__unassigned__') return { conflict: false };
    const reqStart = parseTimeToMinutes(timeHHMM);
    if (reqStart == null) return { conflict: false };
    try {
      const { db } = await import('../config/firebase');
      const { collection, getDocs, query, where } = await import('firebase/firestore');
      const rules = mergeBookingRules((await shopSettingsService.getSettings())?.bookingRules);
      const snap = await getDocs(query(collection(db, 'appointments'),
        where('barberId', '==', barberId), where('date', '==', dateStr)));
      const fresh: any[] = snap.docs.map(d => ({
        id: d.id,
        barberId: (d.data() as any).barberId,
        time: (d.data() as any).time,
        duration: (d.data() as any).duration,
        status: (d.data() as any).status,
      }));
      try {
        const blk = await getDocs(query(collection(db, 'blockedTimes'),
          where('barberId', '==', barberId), where('date', '==', dateStr)));
        for (const d of blk.docs) { const b = d.data() as any; fresh.push({ id: d.id, barberId: b.barberId, time: b.startTime, duration: b.duration || 30, status: 'confirmed' }); }
      } catch { /* blocks read optional — never break a move on a blocks-read failure */ }
      const c = hasApptConflict(barberId, reqStart, duration || 30, fresh, ignoreApptId, rules.noShowOccupiesSlot);
      console.log(`%c[SLIK move-guard]%c ${barberId} @ ${timeHHMM} on ${dateStr} — ${fresh.length} live — ${c.conflict ? '🚫 overlaps ' + (c as any).withTime : '✅ clear'}`,
        'background:#0ea5e9;color:#fff;padding:2px 6px;border-radius:4px;font-weight:bold', 'color:inherit');
      return c.conflict ? { conflict: true, withTime: (c as any).withTime } : { conflict: false };
    } catch (e) {
      console.warn('[SLIK move-guard] fresh read failed — allowing move (fail-open):', e);
      return { conflict: false };
    }
  }

  /**
   * Move ONE appointment to a new (barber, date, time), guarded by the fresh-read conflict check.
   * Used to resolve a single conflicting occurrence of a series reschedule to a time the operator picks.
   * Returns { ok:false, withTime } if the chosen slot is (still) taken — the caller re-prompts. (Jose 2026-10-04)
   */
  async moveAppointmentGuarded(
    id: string, barberId: string, barberName: string, dateStr: string, timeHHMM: string, duration: number,
    allowOverlap: boolean = false, // ⚡ manager squeeze (Jose 2026-10-25): skip the fresh conflict gate to force a taken slot
  ): Promise<{ ok: boolean; withTime?: string }> {
    if (!allowOverlap) {
      const c = await this.checkMoveConflictFresh(barberId, dateStr, timeHHMM, duration, id);
      if (c.conflict) return { ok: false, withTime: c.withTime };
    }
    // conflict check (safety gate) already passed above → background the write so the move lands instantly
    await this.updateAppointment(id, { barberId, barberName, date: dateStr, time: timeHHMM } as any, { background: true });
    return { ok: true };
  }

  async rescheduleAppointment(
    appointmentId: string,
    newDate: Date,
    time: string,
    newBarberId: string,
    applyToSeries: boolean,
    resolveBarberName?: (id: string) => string,
    allowOverlap: boolean = false, // ⚡ manager squeeze-in (Jose 2026-10-20): skip the double-book guard and move onto an occupied slot
  ): Promise<{ moved: number; skipped: number; conflicts: Array<{ id: string; date: string; attemptedTime: string; withTime?: string }>; weekday: string }> {
    const pad = (n: number) => String(n).padStart(2, '0');
    const ymdStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    // Parse "3:00 PM" or "15:00" → hour24/min.
    let hour24: number, minutes: number;
    const m12 = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (m12) {
      hour24 = parseInt(m12[1], 10) % 12;
      if (/PM/i.test(m12[3])) hour24 += 12;
      minutes = parseInt(m12[2], 10);
    } else {
      const [h, m] = time.split(':').map(Number);
      hour24 = h || 0; minutes = m || 0;
    }
    const newStart = new Date(newDate); newStart.setHours(hour24, minutes, 0, 0);
    const formattedTime = `${pad(hour24)}:${pad(minutes)}`;
    const barberName = resolveBarberName ? resolveBarberName(newBarberId)
      : (this.appointments.find(a => a.barberId === newBarberId)?.barberName || 'Unknown');

    const edited = this.appointments.find(a => a.id === appointmentId);
    const groupId = applyToSeries ? (edited as any)?.recurrenceGroupId : undefined;
    // Local datetime from a YMD + time. Handles BOTH "5:30 PM" and "17:30", never UTC-shifts the day,
    // and never yields an Invalid Date on a 12-hour time — the old `new Date(`${date}T${time}:00`)` became
    // "2026-10-23T5:30 PM:00" = Invalid → NaN day-shift silently broke series reschedules. (Jose 2026-10-23)
    const localDT = (dateYmd: string, t?: string): Date => {
      const d = parseYmd(dateYmd);
      const mins = parseTimeToMinutes(t) ?? 0;
      d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
      return d;
    };
    const editedOldStart = edited ? localDT(edited.date, edited.time) : newStart;
    const dayShift = groupId ? (newStart.getDay() - editedOldStart.getDay()) : 0;

    const targets: Array<{ id: string; date: string }> = [{ id: appointmentId, date: ymdStr(newStart) }];
    if (groupId) {
      for (const a of this.appointments) {
        if (a.id === appointmentId || (a as any).recurrenceGroupId !== groupId) continue;
        if (['cancelled', 'completed', 'no-show'].includes(a.status)) continue;
        const sibStart = localDT(a.date, a.time);
        if (isNaN(sibStart.getTime()) || sibStart.getTime() <= editedOldStart.getTime()) continue;
        const sibNew = new Date(sibStart); sibNew.setDate(sibNew.getDate() + dayShift);
        targets.push({ id: a.id, date: ymdStr(sibNew) });
      }
    }
    // DOUBLE-BOOK GUARD on reschedule (Jose 2026-10-03 — was MISSING). Fresh-read conflict-check each
    // target before writing, ignoring the appt itself. A single move that conflicts THROWS (the modal
    // shows the reason); in a SERIES, conflicting weeks are SKIPPED and the rest move, reported back.
    const conflicts: Array<{ id: string; date: string; attemptedTime: string; withTime?: string }> = [];
    let moved = 0;
    for (const t of targets) {
      const appt = this.appointments.find(a => a.id === t.id);
      const dur = appt?.duration || 30;
      // ⚡ Manager squeeze-in bypasses the guard entirely; otherwise a single move that conflicts throws.
      if (!allowOverlap) {
        const c = await this.checkMoveConflictFresh(newBarberId, t.date, formattedTime, dur, t.id);
        if (c.conflict) { conflicts.push({ id: t.id, date: t.date, attemptedTime: formattedTime, withTime: c.withTime }); continue; }
      }
      // guard already ran (or manager-squeeze) → background the write so the reschedule/drag lands instantly
      await this.updateAppointment(t.id, { barberId: newBarberId, barberName, date: t.date, time: formattedTime } as any, { background: true });
      moved++;
    }
    const weekday = newStart.toLocaleDateString('en-US', { weekday: 'long' });
    // A SINGLE move to a taken slot throws so the RebookModal shows the error inline. A SERIES returns
    // its conflicts so the caller can open the "pick another time per week" resolver. (Jose 2026-10-04)
    if (targets.length === 1 && conflicts.length > 0) {
      const w = conflicts[0].withTime ? ` (overlaps ${conflicts[0].withTime})` : '';
      throw new Error(`Conflict: ${barberName} is already booked at ${formattedTime}${w}. Pick another time or barber.`);
    }
    return { moved, skipped: conflicts.length, conflicts, weekday };
  }

  // Update appointment with Firebase sync.
  // opts.background (Jose 2026-10-22): the LOCAL value is applied + shown instantly; when set, return
  // right after that and let the server round-trip + no-show re-eval finish on their own. Used by checkout
  // completion so a stalled connection can't hang "finishing up"/the rebook modal. DOCTRINE: anything
  // already saved locally should not block the UI on the server ACK — let it sync in the background.
  async updateAppointment(id: string, updates: Partial<Appointment>, opts?: { background?: boolean }): Promise<Appointment | null> {

    const index = this.appointments.findIndex(apt => apt.id === id);
    if (index !== -1) {
      // Track every changed field so the snapshot merge doesn't revert it before
      // Firebase confirms the write (was: status only — reassigns/duration reverted).
      const prevPending = this.pendingUpdates.get(id) || {};
      this.pendingUpdates.set(id, { ...prevPending, ...updates });

      this.appointments[index] = { ...this.appointments[index], ...updates };

      this.saveToLocalStorage();
      this.notifyListeners();

      // Sync to Firebase + run the no-show re-eval. Wrapped so opts.background can run it detached.
      const persistToServer = async () => {
        try {
          const { db } = await import('../config/firebase');
          const { doc, updateDoc } = await import('firebase/firestore');
          await updateDoc(doc(db, 'appointments', id), updates);
        } catch (error) {
          console.error('Firebase update failed (keeping optimistic value, will retry):', error);
          // FIX 2026-08-07: do NOT delete the pending update on failure — deleting it silently
          // REVERTED the optimistic status (a no-show flipped back to blue after 3s). Keep the local
          // value and retry the write; the snapshot merge clears the pending flag once Firebase confirms.
          setTimeout(async () => {
            try {
              const { db } = await import('../config/firebase');
              const { doc, updateDoc } = await import('firebase/firestore');
              await updateDoc(doc(db, 'appointments', id), updates as any);
            } catch (retryErr) {
              console.error('Appointment update retry failed (value stays optimistic locally):', retryErr);
            }
          }, 2000);
        }

        // NO-SHOW BLOCK engine (2026-08-07): a no-show or a completed service re-evaluates the
        // client's block. 2 back-to-back no-shows → auto-require prepayment; 3 completed since the
        // block → auto-lift. This is the chokepoint every status change flows through.
        if (updates.status === 'no-show' || updates.status === 'completed' ||
            (updates.status === 'cancelled' && ((updates as any).lateCancel === true || (updates as any).cancelReason === 'late-cancel'))) {
          try { await this.reevaluateNoShowBlock(this.appointments[index]); }
          catch (e) { console.warn('No-show block re-eval failed (non-fatal):', e); }
        }
      };

      if (opts?.background) {
        // Local value already applied + visible → return NOW, let the server sync finish detached.
        persistToServer().catch((e) => console.error('[bg updateAppointment] server sync failed (stays optimistic + retries):', e));
      } else {
        await persistToServer();
      }

      return this.appointments[index];
    }
    return null;
  }

  /**
   * Re-evaluate a client's no-show block from their attendance history (shared/noShowBlock.ts).
   * Blocks by turning ON paymentSettings.requirePaymentUpfront with reason 'no-shows'; auto-lifts
   * after 3 completed services since blockedAt. Best-effort — never throws into the update path.
   */
  private async reevaluateNoShowBlock(appt: Appointment): Promise<void> {
    // IDENTITY = PHONE (Jose 2026-08-07): the same person gets booked under slightly different name
    // spellings / separate walk-in ids ("dum Dumb", "dum dumb", "d d"), but the SAME phone = the same
    // person. Both the client we store the block on AND the attendance history we count MUST key on the
    // normalized phone — clientId-keying let each spelling hold only one no-show, so it never hit 2.
    const digits = (appt.clientPhone || '').replace(/\D/g, '');
    const clientId = (appt as any)?.clientId ? String((appt as any).clientId) : '';

    // Resolve the profile that carries paymentSettings: by id first, else by phone.
    let client: any = clientId ? clientDataService.getClientById(clientId) : null;
    if (!client && digits.length >= 7) {
      const byPhone = clientDataService.getClientsByPhone(digits);
      client = byPhone && byPhone[0];
    }
    if (!client) return; // no profile to store the block on (pure walk-in, no record)

    // Count attendance across EVERY appointment that shares this phone (or the resolved client id),
    // not just the exact clientId on this one appointment.
    const clientDigits = (client.phone || '').replace(/\D/g, '');
    const records = this.appointments
      .filter((a) => {
        const aDigits = (a.clientPhone || '').replace(/\D/g, '');
        const matchPhone = clientDigits.length >= 7 && aDigits === clientDigits;
        const matchId = !!(a as any).clientId && String((a as any).clientId) === String(client.id);
        return matchPhone || matchId;
      })
      .map((a) => {
        // date + time → ms, safely. `new Date(`${date}T${time}:00`)` becomes "...T5:30 PM:00"
        // = Invalid → 0 for every 12-hour time, which silently neutered the no-show window.
        // parseYmd (local midnight) + parseTimeToMinutes (handles 12h AND 24h). (Jose 2026-10-23)
        const _d = parseYmd(a.date);
        const _mins = parseTimeToMinutes(a.time) ?? 0;
        _d.setHours(Math.floor(_mins / 60), _mins % 60, 0, 0);
        const dateMs = _d.getTime();
        // LATE-CANCEL = NO-SHOW (Jose 2026-08-07): a cancellation flagged late counts as a MISSED
        // accountability event, POOLED with no-shows toward the block.
        const late = a.status === 'cancelled' && ((a as any).lateCancel === true || (a as any).cancelReason === 'late-cancel');
        return { status: late ? 'no-show' : (a.status as string), dateMs };
      });

    const ps = client.paymentSettings || { requirePaymentUpfront: false, allowOnlinePayment: true };
    const blockedForNoShows = ps.requirePaymentUpfront && ps.paymentReason === 'no-shows';

    if (!blockedForNoShows) {
      // Not currently blocked → N accountability events (no-show or late-cancel) trips it, where N is
      // the shop's BookingRules.noShowBlockThreshold (default 2). (2026-08-08)
      const rules = mergeBookingRules((await shopSettingsService.getSettings())?.bookingRules);
      if (hitsNoShowBlock(records, rules.noShowBlockThreshold)) {
        await clientDataService.updateClient(clientId, {
          paymentSettings: {
            ...ps,
            requirePaymentUpfront: true,
            paymentReason: 'no-shows',
            noShowNotice: ps.noShowNotice || DEFAULT_NO_SHOW_NOTICE,
            noShowBlockedAt: Date.now(),
          },
        } as any);
        console.log(`🚫 No-show block applied to client ${clientId}`);
      }
    }
    // NO AUTO-LIFT (Jose 2026-08-07): the block stays ON PERMANENTLY until the owner flips the
    // "Require Payment Upfront" master switch OFF in the client profile. (Removed the old
    // 3-completed-services auto-release — Jose wants it manual-only.)
  }

  // Check in appointment
  async checkInAppointment(appointmentId: string): Promise<void> {
    const appointment = this.appointments.find(apt => apt.id === appointmentId);
    if (appointment) {
      appointment.status = 'checked-in';
      appointment.checkedInAt = new Date();

      // Add to queue
      this.addToQueue({
        appointmentId: appointment.id,
        clientName: appointment.clientName,
        clientPhone: appointment.clientPhone,
        service: appointment.service,
        barberId: appointment.barberId,
        barberName: appointment.barberName,
        isWalkIn: false
      });

      this.saveToLocalStorage();
      this.notifyListeners();

      // Update in Firebase
      await this.updateAppointment(appointmentId, {
        status: 'checked-in',
        checkedInAt: new Date()
      });
    }
  }

  // Check in walk-in client
  async checkInWalkIn(clientId: string, service: string = 'Haircut', barberId?: string): Promise<void> {
    const client = clientDataService.getClientById(clientId);
    if (!client) return;

    // Create appointment for walk-in
    const appointment = await this.addAppointment({
      clientId: client.id,
      clientName: `${client.firstName} ${client.lastName}`,
      clientPhone: client.phone,
      barberId: barberId || client.preferredBarber || 'any',
      barberName: barberId || client.preferredBarber || 'Next Available',
      service: service,
      date: ymd(),
      time: new Date().toTimeString().split(' ')[0].substring(0, 5),
      duration: 30,
      price: 35,
      status: 'checked-in',
      source: 'walk-in',
      reminderSent: false,
      checkedInAt: new Date()
    });

    // Add to queue
    this.addToQueue({
      appointmentId: appointment.id,
      clientName: `${client.firstName} ${client.lastName}`,
      clientPhone: client.phone,
      service: service,
      barberId: barberId,
      barberName: barberId || 'Next Available',
      isWalkIn: true
    });

    this.notifyListeners();
  }

  // Queue Management
  getQueue(): QueueItem[] {
    return this.queue;
  }

  addToQueue(item: Omit<QueueItem, 'id' | 'checkedInAt' | 'estimatedWait' | 'status'>): QueueItem {
    const newItem: QueueItem = {
      ...item,
      id: `q-${Date.now()}`,
      checkedInAt: new Date(),
      estimatedWait: this.calculateWaitTime(),
      status: 'waiting'
    };

    this.queue.push(newItem);
    this.saveToLocalStorage();
    this.notifyListeners();

    return newItem;
  }

  updateQueueItem(id: string, updates: Partial<QueueItem>): void {
    const index = this.queue.findIndex(item => item.id === id);
    if (index !== -1) {
      this.queue[index] = { ...this.queue[index], ...updates };
      this.saveToLocalStorage();
      this.notifyListeners();
    }
  }

  removeFromQueue(id: string): void {
    this.queue = this.queue.filter(item => item.id !== id);
    this.saveToLocalStorage();
    this.notifyListeners();
  }

  private calculateWaitTime(): number {
    // Calculate based on queue length and average service time
    const avgServiceTime = 30; // minutes
    const queueLength = this.queue.filter(item => item.status === 'waiting').length;
    return queueLength * avgServiceTime;
  }

  // Hard delete appointment — permanently removes from Firestore and local cache
  async deleteAppointment(id: string): Promise<boolean> {
    const index = this.appointments.findIndex(apt => apt.id === id);
    if (index === -1) return false;

    // Tombstone BEFORE removing so an in-flight snapshot can't resurrect it (the ping-pong).
    this.deletedIds.add(id);
    const removed = this.appointments[index];
    this.appointments.splice(index, 1);
    this.pendingUpdates.delete(id); // any pending update is moot once deleted

    // Remove from queue if present
    const queueItem = this.queue.find(item => item.appointmentId === id);
    if (queueItem) {
      this.removeFromQueue(queueItem.id);
    }

    this.saveToLocalStorage();
    this.notifyListeners();

    // Delete from Firestore
    try {
      const { db } = await import('../config/firebase');
      const { doc, deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(db, 'appointments', id));
      return true;
    } catch (error) {
      console.error('Firebase delete failed — restoring appointment (never silently hide a live doc):', error);
      // The delete did NOT persist: drop the tombstone and put it back so the UI reflects reality.
      this.deletedIds.delete(id);
      if (!this.appointments.some(a => a.id === id)) this.appointments.splice(index, 0, removed);
      this.saveToLocalStorage();
      this.notifyListeners();
      return false;
    }
  }

  // Cancel appointment
  cancelAppointment(id: string): void {
    const appointment = this.appointments.find(apt => apt.id === id);
    if (appointment) {
      appointment.status = 'cancelled';

      // Remove from queue if present
      const queueItem = this.queue.find(item => item.appointmentId === id);
      if (queueItem) {
        this.removeFromQueue(queueItem.id);
      }

      this.saveToLocalStorage();
      this.notifyListeners();
    }
  }

  // Complete appointment
  async completeAppointment(id: string): Promise<void> {
    const appointment = this.appointments.find(apt => apt.id === id);
    if (appointment) {
      appointment.status = 'completed';
      appointment.completedAt = new Date();

      // Remove from queue
      const queueItem = this.queue.find(item => item.appointmentId === id);
      if (queueItem) {
        this.removeFromQueue(queueItem.id);
      }

      // Calculate actual duration if startedAt exists
      // This powers the squeeze-in algorithm — it averages real durations, not service estimates
      const completedAt = new Date();
      const updateData: Record<string, any> = {
        status: 'completed',
        completedAt
      };

      if (appointment.startedAt) {
        const startTime = appointment.startedAt instanceof Date
          ? appointment.startedAt
          : new Date(appointment.startedAt);
        const actualMinutes = Math.round((completedAt.getTime() - startTime.getTime()) / 60000);
        if (actualMinutes > 0 && actualMinutes < 240) { // Sanity check: between 1 min and 4 hours
          updateData.actualDuration = actualMinutes;
          // Update the duration field so the squeeze-in algorithm reads real data
          updateData.duration = actualMinutes;
        }
      }

      await this.updateAppointment(id, updateData);

      // 🔄 AUTO-UPDATE CLIENT STATS & RECALCULATE CPI
      // This will update: totalVisits, totalSpent, averageTicket, lastVisit, rebookingRate, etc.
      // CPI will be automatically recalculated based on these updated stats
      await clientDataService.updateClientStatsFromAppointments(appointment.clientId);

      this.saveToLocalStorage();
      this.notifyListeners();
    }
  }

  // Get appointment statistics
  getStats() {
    const today = ymd();
    const todaysAppointments = this.getAppointmentsByDate(today);

    return {
      totalToday: todaysAppointments.length,
      checkedIn: todaysAppointments.filter(apt => apt.status === 'checked-in').length,
      completed: todaysAppointments.filter(apt => apt.status === 'completed').length,
      cancelled: todaysAppointments.filter(apt => apt.status === 'cancelled').length,
      noShow: todaysAppointments.filter(apt => apt.status === 'no-show').length,
      queueLength: this.queue.filter(item => item.status === 'waiting').length,
      avgWaitTime: this.calculateWaitTime(),
      totalRevenue: todaysAppointments
        .filter(apt => apt.status === 'completed')
        .reduce((sum, apt) => sum + apt.price, 0)
    };
  }

  // Persistence
  private saveToLocalStorage(): void {
    // localStorage is a BEST-EFFORT offline cache — Firestore is the source of truth. It has a hard
    // ~5MB limit and NO garbage collection, so stringifying the full appointment history eventually
    // throws "The quota has been exceeded" (Jose 2026-08-30) — which was propagating up and KILLING
    // real sale writes (charge taken, record lost). A full cache must NEVER break a sale. So: try the
    // full save; on overflow, keep only a recent window + all upcoming and retry; if still full, skip.
    try {
      localStorage.setItem('slikpos_appointments', JSON.stringify(this.appointments));
      localStorage.setItem('slikpos_queue', JSON.stringify(this.queue));
    } catch (e) {
      try {
        const cutoff = Date.now() - 120 * 86400000; // keep ~120 days of history
        const trimmed = this.appointments.filter((a: any) => {
          if (a.status === 'scheduled' || a.status === 'confirmed' || a.status === 'checked-in' || a.status === 'in-progress') return true; // all upcoming
          const t = new Date(((a.date as string) || '1970-01-01') + 'T00:00:00').getTime();
          return isNaN(t) || t >= cutoff;
        });
        localStorage.setItem('slikpos_appointments', JSON.stringify(trimmed));
        localStorage.setItem('slikpos_queue', JSON.stringify(this.queue));
      } catch {
        // Still over quota — drop the local cache entirely. Firestore already has the write; the
        // cache just rebuilds from the next snapshot. NEVER let this throw into the checkout path.
        try { localStorage.removeItem('slikpos_appointments'); } catch { /* noop */ }
        console.warn('[appointmentService] localStorage over quota — skipped local cache (Firestore is source of truth)');
      }
    }
  }

  private loadFromLocalStorage(): void {
    const appointmentsData = localStorage.getItem('slikpos_appointments');
    const queueData = localStorage.getItem('slikpos_queue');

    if (appointmentsData) {
      this.appointments = JSON.parse(appointmentsData);
    }

    if (queueData) {
      this.queue = JSON.parse(queueData);
    }
  }

  // Clear old appointments (maintenance)
  clearOldAppointments(daysToKeep: number = 30): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

    this.appointments = this.appointments.filter(apt => {
      const aptDate = parseYmd(apt.date); // LOCAL date — avoid the UTC off-by-one that could evict a day early
      return aptDate >= cutoffDate;
    });

    this.saveToLocalStorage();
    this.notifyListeners();
  }

  // ============================================
  // GEO-LOCATION / ETA TRACKING
  // ============================================

  /**
   * Update geo-location data on an appointment (called by customer portal ETA tracking)
   */
  async updateGeoLocation(appointmentId: string, geoData: Partial<NonNullable<Appointment['geoLocation']>>): Promise<void> {
    const appointment = this.appointments.find(apt => apt.id === appointmentId);
    if (!appointment) return;

    const updatedGeo: NonNullable<Appointment['geoLocation']> = {
      enabled: false,
      ...appointment.geoLocation,
      ...geoData,
    };
    await this.updateAppointment(appointmentId, { geoLocation: updatedGeo });
  }

  /**
   * Handle staff response to a late arrival
   */
  async handleLateArrival(
    appointmentId: string,
    response: 'keep-slot' | 'take-walkin' | 'reschedule',
    rescheduledTo?: string
  ): Promise<void> {
    const geoUpdate: Partial<NonNullable<Appointment['geoLocation']>> = {
      staffResponse: response,
      staffResponseAt: new Date(),
    };

    if (response === 'reschedule' && rescheduledTo) {
      geoUpdate.rescheduledTo = rescheduledTo;
    }

    await this.updateGeoLocation(appointmentId, geoUpdate);

    // If "take walk-in", free the slot by cancelling
    if (response === 'take-walkin') {
      await this.updateAppointment(appointmentId, { status: 'cancelled' });
    }
  }

  /**
   * Reverse a checkout — "the wrong person was checked out" (Jose 2026-11-03). Strips the whole sale off
   * the appointment and either puts it BACK on the schedule (reactivate → status 'scheduled', a clean
   * upcoming appt) or leaves it cancelled. The CARD money-back (Stripe) is done by the caller via
   * refundSale when a stripePaymentIntentId exists; a cash / terminal sale is squared physically. Clearing
   * the money fields also pulls it out of reporting (which keys off status==='completed') and drops the
   * "Paid" chip + receipt. Returns what the sale carried so the caller can refund the card + warn when
   * coins / gift were used (NOT auto-restored in v1). Gated at the UI by the 'refundVoid' permission.
   */
  async reverseCheckout(
    id: string,
    opts: { reactivate: boolean; reason?: string }
  ): Promise<{ ok: boolean; hadCardPI: boolean; coinsUsed: number; giftApplied: number; amount: number; error?: string }> {
    const fail = (error: string) => ({ ok: false, hadCardPI: false, coinsUsed: 0, giftApplied: 0, amount: 0, error });
    try {
      const { db } = await import('../config/firebase');
      const { doc, getDoc, updateDoc, deleteField, serverTimestamp } = await import('firebase/firestore');
      const ref = doc(db, 'appointments', id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return fail('not-found');
      const a: any = snap.data();
      const coinsUsed = Number(a.slikCoinsUsed || a.slikCoinsRedeemed || 0) || 0;
      const giftApplied = Number(a.giftCardRedeemApplied || 0) || 0;
      const amount = Number(a.amountCharged ?? a.ticketTotal ?? a.total ?? 0) || 0;
      const hadCardPI = !!(a.stripePaymentIntentId || a.paymentIntentId);

      await updateDoc(ref, {
        amountCharged: deleteField(), tipAmount: deleteField(), ticketTotal: deleteField(), total: deleteField(),
        paymentMethod: deleteField(), taxAmount: deleteField(), taxRatePct: deleteField(),
        completedAt: deleteField(), checkoutAt: deleteField(), paidOnline: deleteField(),
        slikCoinDiscount: deleteField(), slikCoinsUsed: deleteField(), slikCoinsRedeemed: deleteField(),
        giftCardRedeemApplied: deleteField(), giftCardCode: deleteField(), giftCardBalanceBefore: deleteField(),
        splitCashAmount: deleteField(), productsSold: deleteField(),
        status: opts.reactivate ? 'scheduled' : 'cancelled',
        reversedAt: serverTimestamp(),
        reversedReason: opts.reason || (opts.reactivate ? 'wrong checkout — reactivated' : 'checkout reversed'),
        ...(opts.reactivate ? { reactivatedAt: serverTimestamp() } : { voidedAt: serverTimestamp() }),
        updatedAt: serverTimestamp(),
      });
      return { ok: true, hadCardPI, coinsUsed, giftApplied, amount };
    } catch (e: any) {
      console.error('[reverseCheckout] failed', e);
      return fail(e?.message || 'reverse-failed');
    }
  }

  /**
   * Insert a walk-in into a freed slot
   */
  async insertWalkIn(
    barberId: string,
    barberName: string,
    clientName: string,
    clientPhone: string,
    service: string,
    time: string
  ): Promise<Appointment> {
    const today = ymd();
    return this.addAppointment({
      clientId: `walkin-${Date.now()}`,
      clientName,
      clientPhone,
      barberId,
      barberName,
      service,
      date: today,
      time,
      duration: 30,
      price: 35,
      status: 'checked-in',
      source: 'walk-in',
      reminderSent: false,
      checkedInAt: new Date(),
    });
  }

  // ============================================
  // SMART EMERGENCY BOOKING - Shrink & Inject
  // ============================================

  /**
   * Smart Emergency Injection
   *
   * Shrinks an existing appointment to make room for emergency booking.
   * Example: 3:00 PM 30-min appointment becomes:
   *   - 3:00-3:15 (original, shrunk)
   *   - 3:15-3:30 (emergency, injected)
   *
   * @param targetTime - Time of existing appointment to squeeze into (HH:MM)
   * @param barberId - Barber ID
   * @param emergencyAppointment - Emergency appointment data
   * @param options - Configuration options
   * @returns Success/failure with details
   */
  async squeezeInEmergency(
    targetTime: string,
    barberId: string,
    emergencyAppointment: Omit<Appointment, 'id' | 'createdAt'>,
    options?: {
      minDuration?: number; // Minimum duration for shrunk appointment (default 15)
      emergencyDuration?: number; // Emergency booking duration (default 15)
      allowConflicts?: boolean; // Allow overlaps (default false)
    }
  ): Promise<{
    success: boolean;
    message: string;
    originalAppointment?: Appointment;
    emergencyAppointment?: Appointment;
    warnings?: string[];
    aiRecommendation?: {
      recommendation: string;
      confidence: number;
      reasoning: string;
      warnings?: string[];
      suggestions?: string[];
    };
  }> {
    // Get slot interval from shop settings so squeeze-in times snap to grid
    let slotInterval = 15;
    try {
      const settings = await shopSettingsService.getSettings();
      if (settings?.bookingSettings?.slotInterval) {
        slotInterval = settings.bookingSettings.slotInterval;
      }
    } catch { /* use default 15 */ }

    const minDuration = options?.minDuration || slotInterval; // Snap to slot interval
    const emergencyDuration = options?.emergencyDuration || slotInterval;
    const allowConflicts = options?.allowConflicts || false;

    const warnings: string[] = [];

    try {
      // Step 1: Find the target appointment at this time/barber
      const targetAppointment = this.appointments.find(apt =>
        apt.time === targetTime &&
        apt.barberId === barberId &&
        apt.status !== 'cancelled' &&
        apt.status !== 'completed'
      );

      if (!targetAppointment) {
        // FREE SLOT (2026-08-06): there's no existing appointment to squeeze around, so this
        // isn't a squeeze — it's a plain emergency booking on an OPEN slot. Book it directly at
        // the target time. (The Emergency tile already validated the slot against lunch / blocks /
        // working hours before calling, so a bad slot never reaches here.)
        const directApt = await this.addAppointment({
          ...emergencyAppointment,
          time: targetTime,
          duration: emergencyDuration,
        });
        return {
          success: true,
          message: `Emergency booking created at ${targetTime}`,
          emergencyAppointment: directApt,
        };
      }

      // Step 1.5: Check if this client's appointments are protected from squeezing
      // Import clientDataService dynamically to avoid circular dependency
      const { clientDataService } = await import('./clientDataService');
      if (targetAppointment.clientId) {
        const client = await clientDataService.getClientById(targetAppointment.clientId);
        if (client?.emergencyBookingSettings?.blockSqueezing) {
          return {
            success: false,
            message: `Cannot squeeze - ${client.firstName}'s appointments are protected from emergency squeezing`,
            warnings: ['Client has "Block Emergency Squeezing" enabled in their profile']
          };
        }
      }

      // Step 2: Check if already squeezed
      if (targetAppointment.duration && targetAppointment.duration <= minDuration) {
        return {
          success: false,
          message: `Appointment already squeezed to ${targetAppointment.duration} minutes - cannot shrink further`
        };
      }

      // Step 3: Check for existing emergency booking at injection point
      // Snap injection time to grid so cards always align with calendar lines
      const rawInjectionTime = this.addMinutesToTime(targetTime, minDuration);
      // Snap to the SHRINK granularity (minDuration), NOT the coarse shop slotInterval. At a 30-min
      // slotInterval, snapping 11:15 to the 30-grid rounded it to 11:30 — flinging the emergency off
      // its squeeze point. The injection is intentionally on a minDuration (15-min) boundary, and the
      // calendar now offers 15-min drop targets, so this stays aligned. (Jose 2026-10-03)
      const injectionTime = this.snapTimeToGrid(rawInjectionTime, minDuration);
      const existingEmergency = this.appointments.find(apt =>
        apt.time === injectionTime &&
        apt.barberId === barberId &&
        apt.status !== 'cancelled'
      );

      if (existingEmergency) {
        return {
          success: false,
          message: `Emergency booking already exists at ${injectionTime} - only one squeeze-in per slot allowed`
        };
      }

      // Step 4: Check if appointment AFTER would conflict
      const emergencyEndTime = this.addMinutesToTime(injectionTime, emergencyDuration);
      const nextAppointment = this.appointments.find(apt =>
        apt.barberId === barberId &&
        apt.time >= emergencyEndTime &&
        apt.time < this.addMinutesToTime(emergencyEndTime, 30) && // within 30 min window
        apt.status !== 'cancelled'
      );

      if (nextAppointment && !allowConflicts) {
        const gap = this.calculateMinutesBetween(emergencyEndTime, nextAppointment.time);
        if (gap < 5) {
          warnings.push(`Very tight buffer (${gap} min) before next appointment at ${nextAppointment.time}`);
        }
      }

      // AI-powered smart analysis for emergency booking
      let aiRecommendation;
      try {
        const barber = emergencyAppointment.barberName || 'Unknown';
        const urgencyLevel = emergencyAppointment.urgencyLevel || 'urgent';

        aiRecommendation = await localAIService.analyzeEmergencyBooking({
          targetTime,
          originalDuration: targetAppointment.duration || 30,
          shrunkDuration: minDuration,
          emergencyDuration,
          nextAppointmentTime: nextAppointment?.time,
          bufferMinutes: nextAppointment ? this.calculateMinutesBetween(emergencyEndTime, nextAppointment.time) : undefined,
          barberName: barber,
          urgencyLevel: urgencyLevel as 'standard' | 'urgent' | 'critical'
        });

        // Add AI warnings to main warnings array
        if (aiRecommendation.warnings) {
          warnings.push(...aiRecommendation.warnings);
        }
      } catch {
        // AI analysis unavailable, proceed with rule-based decision
      }

      // Shrink the original appointment
      const originalDuration = targetAppointment.duration || 30;
      await this.updateAppointment(targetAppointment.id, {
        duration: minDuration,
        notes: (targetAppointment.notes || '') + ` [SQUEEZED from ${originalDuration}min to ${minDuration}min for emergency booking]`
      });

      // Inject emergency appointment
      const newEmergencyApt = await this.addAppointment({
        ...emergencyAppointment,
        time: injectionTime,
        duration: emergencyDuration,
        notes: `🚨 EMERGENCY BOOKING - Squeezed into ${targetTime} slot. ${emergencyAppointment.notes || ''}`
      });

      return {
        success: true,
        message: `Successfully squeezed emergency booking into ${targetTime} slot`,
        originalAppointment: this.appointments.find(a => a.id === targetAppointment.id),
        emergencyAppointment: newEmergencyApt,
        warnings: warnings.length > 0 ? warnings : undefined,
        aiRecommendation: aiRecommendation
      };

    } catch (error) {
      console.error('Error in squeezeInEmergency:', error);
      return {
        success: false,
        message: `Failed to create emergency booking: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Add minutes to time string (HH:MM format)
   */
  private addMinutesToTime(time: string, minutesToAdd: number): string {
    const [hours, minutes] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + minutesToAdd;
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
  }

  /**
   * Snap a time string to the nearest slot interval boundary
   * e.g. "09:35" with interval 15 → "09:30", "09:38" with interval 15 → "09:45"
   */
  private snapTimeToGrid(time: string, intervalMinutes: number): string {
    const [hours, minutes] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    const snapped = Math.round(totalMinutes / intervalMinutes) * intervalMinutes;
    const newHours = Math.floor(snapped / 60);
    const newMinutes = snapped % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
  }

  /**
   * Calculate minutes between two times
   */
  private calculateMinutesBetween(time1: string, time2: string): number {
    const [h1, m1] = time1.split(':').map(Number);
    const [h2, m2] = time2.split(':').map(Number);
    const minutes1 = h1 * 60 + m1;
    const minutes2 = h2 * 60 + m2;
    return Math.abs(minutes2 - minutes1);
  }
}

// Export singleton instance
export const appointmentService = new AppointmentService();